import React, { useState, useEffect } from 'react';
import { HeadlineGroup, Insight } from '@/types';
import NewsStream from '@/components/panel/NewsStream';
import { useStore } from '@/store';
import StreamVisualization from '@/components/panel/StreamVisualization';
import { InsightsTable } from '@/components/panel/InsightsTable';
import { UnifiedSelectionHeader } from '@/components/layout/UnifiedSelectionHeader';
import { ConfigPanel } from '@/components/panel/ConfigPanel';
import MainHeader from '@/components/layout/MainHeader';
import { useAuth } from '@/contexts/AuthContext';
import SearchPanel from '@/components/panel/SearchPanel';

interface MainPanelProps {
  onHeadlineGroupSelect: (group: HeadlineGroup) => void;
  showLoginInHeader?: boolean;
  initiateGoogleLogin?: () => void;
}

export default function MainPanel({ 
  onHeadlineGroupSelect, 
  showLoginInHeader = false,
  initiateGoogleLogin
}: MainPanelProps) {
  const { 
    selectedTab,
    selectedStreamIcon, 
    selectedStudioIcon
  } = useStore();
  const { userProfile, handleLogout } = useAuth();
  const [selectedInsight, setSelectedInsight] = useState<Insight | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Handle insight selection
  const handleInsightSelect = (insight: Insight) => {
    setSelectedInsight(insight);
    // Additional logic for handling insight selection can be added here
  };

  // Handle search focus change
  const handleSearchFocusChange = (isFocused: boolean) => {
    setIsSearchFocused(isFocused);
  };

  // Handle search query change
  const handleSearchQueryChange = (query: string) => {
    setSearchQuery(query);
  };

  // Check for mobile screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="flex flex-col h-screen">
      {/* Main Header with Logo, Tabs and Search */}
      <MainHeader 
        showLoginInHeader={showLoginInHeader}
        initiateGoogleLogin={initiateGoogleLogin}
        userProfile={userProfile}
        handleLogout={handleLogout}
        onSearchFocusChange={handleSearchFocusChange}
        onSearchQueryChange={handleSearchQueryChange}
      />

      {/* Second row: Unified Selection Header with icons - Hidden when search is focused */}
      {!isSearchFocused && (
        <div className="border-b py-1 px-4">
          <UnifiedSelectionHeader 
            type={selectedTab === 'stream' ? 'stream' : 'studio'} 
          />
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 overflow-hidden">
        {isSearchFocused ? (
          <SearchPanel searchQuery={searchQuery} />
        ) : selectedTab === 'stream' && selectedStreamIcon === 'visualization' ? (
          <StreamVisualization />
        ) : selectedTab === 'stream' && selectedStreamIcon === 'news' ? (
          <NewsStream onHeadlineGroupSelect={onHeadlineGroupSelect} />
        ) : selectedTab === 'stream' && selectedStreamIcon === 'config' ? (
          <ConfigPanel type="stream" />
        ) : selectedTab === 'studio' && selectedStudioIcon === 'insights' ? (
          <div className="p-4 h-full">
            <h3 className="text-lg font-medium mb-2">Insights</h3>
            <InsightsTable onInsightSelect={handleInsightSelect} />
          </div>
        ) : selectedTab === 'studio' && selectedStudioIcon === 'config' ? (
          <ConfigPanel type="studio" />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            Select an option to view content
          </div>
        )}
      </div>
    </div>
  );
} 